import { useState, type ReactElement } from 'react';
import { ErrorCode } from '@gr/core/errors';
import { useT } from '../../../../app/di';
import { Alert } from '../../../../ui-kit/Alert/Alert';
import { Button } from '../../../../ui-kit/Button/Button';
import { FormAlert, FormTextField, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';

export interface PasswordChange {
  currentPassword: string;
  newPassword: string;
}

/** IAM-UC-4: смена своего пароля; после успеха поля очищаются. */
export function PasswordForm({ onSubmit }: { onSubmit(change: PasswordChange): Promise<ServerOutcome> }): ReactElement {
  const { t } = useT();
  const [done, setDone] = useState(false);
  const empty: PasswordChange = { currentPassword: '', newPassword: '' };
  const { form, submit } = useServerForm<PasswordChange>({
    defaultValues: empty,
    fields: ['currentPassword', 'newPassword'],
    onSubmit: async (change) => {
      setDone(false);
      const outcome = await onSubmit(change);
      setDone(outcome.success);
      return outcome;
    },
    afterSuccess: () => empty,
  });
  const { control, formState } = form;
  const required = { required: ErrorCode.FieldRequired };
  return (
    <form className="u-stack u-measure-md" onSubmit={submit} noValidate>
      {done ? <Alert tone="success">{t('identity.profile.passwordChanged')}</Alert> : null}
      <FormAlert form={form} />
      <FormTextField
        control={control}
        name="currentPassword"
        label={t('identity.profile.currentPassword')}
        type="password"
        autoComplete="current-password"
        rules={required}
        disabled={formState.isSubmitting}
      />
      <FormTextField
        control={control}
        name="newPassword"
        label={t('identity.profile.newPassword')}
        type="password"
        autoComplete="new-password"
        hint={t('identity.profile.passwordHint')}
        rules={required}
        disabled={formState.isSubmitting}
      />
      <div>
        <Button type="submit" variant="primary" loading={formState.isSubmitting}>
          {t('identity.profile.changePassword')}
        </Button>
      </div>
    </form>
  );
}
